import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import SiteSettingsTab from "@/pages/admin/SiteSettingsTab";
import { toast } from "sonner";
import { BadgeCheck, Shield, Users, Mail, Calendar, Star, Trash2, Check, X, RefreshCw, Settings } from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: Shield },
  { key: "doctors", label: "Doctors", icon: Users },
  { key: "registrations", label: "Registrations", icon: BadgeCheck },
  { key: "contacts", label: "Contact requests", icon: Mail },
  { key: "appointments", label: "Appointments", icon: Calendar },
  { key: "settings", label: "Site settings", icon: Settings },
];

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-display text-3xl font-bold mt-1 tabular">{value ?? "—"}</p>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [regs, setRegs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [appts, setAppts] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = async () => {
    setRefreshing(true);
    try {
      const [s, d, r, c, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/doctors"),
        api.get("/admin/registrations"),
        api.get("/admin/contact-requests"),
        api.get("/admin/appointments"),
      ]);
      setStats(s.data);
      setDoctors(d.data.items || []);
      setRegs(r.data.items || []);
      setContacts(c.data.items || []);
      setAppts(a.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") loadAll();
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const toggleVerified = async (doc) => {
    try {
      await api.put(`/admin/doctors/${doc.id}/verified`, { value: !doc.verified });
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const toggleFeatured = async (doc) => {
    try {
      await api.put(`/admin/doctors/${doc.id}/featured`, { value: !doc.featured });
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const setStatus = async (doc, status) => {
    try {
      await api.put(`/admin/doctors/${doc.id}/status`, { status });
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteDoctor = async (doc) => {
    if (!window.confirm(`Delete ${doc.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/doctors/${doc.id}`);
      toast.success("Doctor removed.");
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const approveReg = async (reg) => {
    try {
      await api.post(`/admin/registrations/${reg.id}/approve`);
      toast.success("Approved. Doctor is now live.");
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const rejectReg = async (reg) => {
    try {
      await api.post(`/admin/registrations/${reg.id}/reject`);
      toast.success("Registration rejected.");
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section className="mx-auto max-w-[1280px] px-4 lg:px-6 py-8">
      <Seo title="Admin dashboard" canonicalPath="/admin" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Admin dashboard</h1>
          <p className="text-muted-foreground text-sm">Welcome back, {user.name}.</p>
        </div>
        <button onClick={loadAll} className="btn-secondary" data-testid="admin-refresh">
          <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <nav className="mt-6 flex gap-1 overflow-x-auto no-scrollbar border-b border-border">
        {TABS.map((t) => {
          const I = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${
                active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`tab-${t.key}`}
            >
              <I size={16} /> {t.label}
            </button>
          );
        })}
      </nav>

      {tab === "overview" && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-6">
          <Stat label="Total doctors" value={stats?.doctors} />
          <Stat label="Approved" value={stats?.approved_doctors} />
          <Stat label="Pending registrations" value={stats?.pending_registrations} />
          <Stat label="Appointments" value={stats?.appointments} />
          <Stat label="Contact requests" value={stats?.contact_requests} />
        </div>
      )}

      {tab === "doctors" && (
        <div className="mt-6 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Specialty</th>
                <th className="px-4 py-3 font-semibold">Area</th>
                <th className="px-4 py-3 font-semibold">Fee</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Flags</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {doctors.map((d) => (
                <tr key={d.id} className="border-t border-border" data-testid={`row-doctor-${d.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/doctor/${d.slug}`} className="font-semibold hover:text-primary">{d.name}</Link>
                    <p className="text-xs text-muted-foreground">{d.clinic_name}</p>
                  </td>
                  <td className="px-4 py-3 capitalize">{d.specialty_slug.replace(/-/g, " ")}</td>
                  <td className="px-4 py-3 capitalize">{d.area_slug.replace(/-/g, " ")}</td>
                  <td className="px-4 py-3 tabular">₹{d.consultation_fee}</td>
                  <td className="px-4 py-3">
                    <select value={d.status} onChange={(e) => setStatus(d, e.target.value)} className="rounded-md border border-input bg-white px-2 py-1 text-xs" data-testid={`status-${d.id}`}>
                      <option value="pending">Pending</option>
                      <option value="under_review">Under review</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleVerified(d)} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full font-semibold ${d.verified ? "bg-accent text-[hsl(var(--ld-jade-fg))]" : "bg-muted text-muted-foreground"}`} data-testid={`verify-${d.id}`}>
                        <BadgeCheck size={12} /> {d.verified ? "Verified" : "Not verified"}
                      </button>
                      <button onClick={() => toggleFeatured(d)} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full font-semibold ${d.featured ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`} data-testid={`feature-${d.id}`}>
                        <Star size={12} /> {d.featured ? "Featured" : "Feature"}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => deleteDoctor(d)} className="btn-ghost text-destructive" data-testid={`delete-${d.id}`}>
                      <Trash2 size={14} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
              {doctors.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No doctors yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "registrations" && (
        <div className="mt-6 grid gap-4">
          {regs.length === 0 && <p className="text-muted-foreground text-sm">No registration requests.</p>}
          {regs.map((r) => (
            <article key={r.id} className="rounded-xl border border-border p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid={`reg-${r.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-semibold">{r.name}</h3>
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-accent text-[hsl(var(--ld-jade-fg))]" : r.status === "rejected" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}>{r.status}</span>
                </div>
                <p className="text-sm text-muted-foreground capitalize mt-1">
                  {r.specialty_slug?.replace(/-/g, " ")} · {r.clinic_name} · {r.area_slug?.replace(/-/g, " ")}
                </p>
                <p className="text-xs text-muted-foreground mt-1 tabular">{r.phone} · {r.email}</p>
              </div>
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <button onClick={() => approveReg(r)} className="btn-primary" data-testid={`approve-${r.id}`}><Check size={16} /> Approve</button>
                  <button onClick={() => rejectReg(r)} className="btn-secondary text-destructive" data-testid={`reject-${r.id}`}><X size={16} /> Reject</button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {tab === "contacts" && (
        <div className="mt-6 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Patient</th>
                <th className="px-4 py-3 font-semibold">Doctor</th>
                <th className="px-4 py-3 font-semibold">Phone</th>
                <th className="px-4 py-3 font-semibold">Message</th>
                <th className="px-4 py-3 font-semibold">When</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="px-4 py-3 font-semibold">{c.name}</td>
                  <td className="px-4 py-3">{c.doctor_name}</td>
                  <td className="px-4 py-3 tabular">{c.phone}</td>
                  <td className="px-4 py-3 max-w-sm truncate text-muted-foreground">{c.message || "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground tabular">{new Date(c.created_at).toLocaleString("en-IN")}</td>
                </tr>
              ))}
              {contacts.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No contact requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "appointments" && (
        <div className="mt-6 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Patient</th>
                <th className="px-4 py-3 font-semibold">Doctor</th>
                <th className="px-4 py-3 font-semibold">Slot</th>
                <th className="px-4 py-3 font-semibold">OTP</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {appts.map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <p className="font-semibold">{a.patient_name}</p>
                    <p className="text-xs text-muted-foreground tabular">{a.patient_phone}</p>
                  </td>
                  <td className="px-4 py-3">{a.doctor_name}</td>
                  <td className="px-4 py-3 text-xs tabular">{new Date(a.slot_datetime).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 font-display tabular tracking-widest font-bold">{a.queue_otp}</td>
                  <td className="px-4 py-3 capitalize">{a.status}</td>
                </tr>
              ))}
              {appts.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No appointments booked yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "settings" && <SiteSettingsTab />}
    </section>
  );
}
